import { useReducer, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import MessageAlert from 'app/components/MessageAlert'
import * as R from 'ramda'
import { errorTypes } from './error'
import { useSearch } from 'app/components/searchBar/useSearch'
import useDeepCompareEffect from 'use-deep-compare-effect'
import equal from 'fast-deep-equal'
import { errorCode } from 'app/constants/errorCode'

const pageInfoActionType = {
  LOAD_START: 'LOAD_START',
  LOAD_END: 'LOAD_END',
  LOAD_SUCCESS: 'LOAD_SUCCESS',
  LOAD_FAILURE: 'LOAD_FAILURE',
  TOGGLE_SELECT: 'TOGGLE_SELECT',
  TOGGLE_SELECT_ALL: 'TOGGLE_SELECT_ALL',
  SET_FETCH_INIT_NO_DATA: 'SET_FETCH_INIT_NO_DATA',
}

const DEFAULT_PAGE_SIZE = 10

export const pageInfoActions = {
  loadStart: () => ({ type: pageInfoActionType.LOAD_START }),
  loadEnd: () => ({ type: pageInfoActionType.LOAD_END }),
  loadSuccess: (data, nextPageInfo, total) => ({
    type: pageInfoActionType.LOAD_SUCCESS,
    data,
    total: Math.ceil(total / nextPageInfo.pageSize),
    totalData: total,
    nextPageInfo,
  }),
  loadFailure: (error) => ({
    type: pageInfoActionType.LOAD_FAILURE,
    error,
  }),
  toggleSelect: (id) => ({
    type: pageInfoActionType.TOGGLE_SELECT,
    id,
  }),
  toggleSelectAll: (checked) => ({
    type: pageInfoActionType.TOGGLE_SELECT_ALL,
    checked,
  }),
  setInitFetchNodata: (hasNoDataAtTheFirstTime) => ({
    type: pageInfoActionType.SET_FETCH_INIT_NO_DATA,
    hasNoDataAtTheFirstTime,
  }),
}

const initialState = {
  isOneTimeFetched: false,
  isLoading: false,
  page: undefined,
  pageSize: undefined,
  total: -1,
  data: undefined,
  sorted: undefined,
  filtered: undefined,
  checkedIds: [],
  isCheckedAll: false,
  isIndeterminated: false,
  totalData: 0,
  isFirsttimeFetchingNoData: null,
}

const tableAPIPage = (pageInfo, searchText, filter) => {
  return {
    ...pageInfo,
    currentPage: R.inc(R.propOr(0, 'page', pageInfo)),
    keyword: searchText,
    ...filter,
  }
}

const pageInfoReducer = (state, action) => {
  switch (action.type) {
    case pageInfoActionType.LOAD_START: {
      return {
        ...state,
        isLoading: true,
      }
    }
    case pageInfoActionType.LOAD_END: {
      return {
        ...state,
        isLoading: false,
        isOneTimeFetched: true,
      }
    }
    case pageInfoActionType.LOAD_FAILURE: {
      return {
        ...state,
        error: action.error,
      }
    }
    case pageInfoActionType.LOAD_SUCCESS: {
      const { nextPageInfo, data, total, totalData } = action
      const checkedIds = R.filter(
        R.includes(R.__, R.map(R.prop('id'), data)),
        state.checkedIds,
      )

      const getIsFirsttimeFetchingNoData = () => {
        return state.isFirsttimeFetchingNoData === null
          ? totalData === 0
          : state.isFirsttimeFetchingNoData
      }

      return {
        ...state,
        ...nextPageInfo,
        error: null,
        data,
        total,
        totalData,
        checkedIds,
        isFirsttimeFetchingNoData: getIsFirsttimeFetchingNoData(),
        ...checkedProps(checkedIds, data),
      }
    }
    case pageInfoActionType.TOGGLE_SELECT: {
      const checkedIds = R.ifElse(
        R.includes(R.__, state.checkedIds),
        R.o(R.without(R.__, state.checkedIds), R.of),
        R.append(R.__, state.checkedIds),
      )(action.id)
      return {
        ...state,
        checkedIds,
        ...checkedProps(checkedIds, state.data),
      }
    }
    case pageInfoActionType.TOGGLE_SELECT_ALL: {
      const checkedIds = action.checked ? R.map(R.prop('id'), state.data) : []
      return {
        ...state,
        checkedIds,
        ...checkedProps(checkedIds, state.data),
      }
    }
    case pageInfoActionType.SET_FETCH_INIT_NO_DATA: {
      return {
        ...state,
        isFirsttimeFetchingNoData: action.hasNoDataAtTheFirstTime,
      }
    }
    default:
      return state
  }
}

const checkedProps = (checkedIds, data) => {
  const isCheckedAll = isCheckAll(checkedIds, data)
  return {
    checkedIds,
    isCheckedAll,
    isIndeterminated: isIndeterminated(checkedIds, isCheckedAll),
    checkedData: checkedData(checkedIds, data),
  }
}

const isCheckAll = (checkedIds, data) =>
  R.ifElse(
    R.isEmpty,
    R.always(false),
    R.eqBy(R.length, checkedIds),
  )(R.defaultTo([], data))

const checkedData = (checkedIds, data) =>
  R.filter(R.propSatisfies(R.includes(R.__, checkedIds), 'id'), data)

const isIndeterminated = (checkedIds, isCheckedAll) =>
  !R.isEmpty(checkedIds) && !isCheckedAll

export const useTableFetch = (fetchData, initialFilter = {}) => {
  const dispatch = useDispatch()
  const { searchText, debounceSearchText, onChange, onReset, onClearSearch } =
    useSearch()
  const [filter, setFilter] = useState(initialFilter)
  const { formatMessage } = useIntl()
  const [pageInfo, dispatchPageInfo] = useReducer(pageInfoReducer, initialState)

  const onChangeFilter = (values) => {
    setFilter((prev) => R.mergeAll([prev, values]))
  }

  const onChangeFilterByKey = (key) => (e) => {
    const value = R.pathOr(e, ['target', 'value'], e)
    setFilter({ ...filter, [key]: value })
  }

  const showErrorDialogue = (error) => {
    const codeError = R.path(['response', 'data', 'details', 'code'], error)
    const excludedLoadFailure = [errorCode.roleNameAlreadyExists]

    if (!excludedLoadFailure.includes(codeError))
      dispatchPageInfo(pageInfoActions.loadFailure(error))
    dispatch(
      MessageAlert.showNetworkError(formatMessage)(error, errorTypes.default),
    )
  }

  const reloadData = (pageInfo, searchText, filter) => {
    if (fetchData) {
      let fetchedData = fetchData(tableAPIPage(pageInfo, searchText, filter))
      if (fetchedData) {
        return fetchedData
          .then(({ data, meta }) => {
            dispatchPageInfo(
              pageInfoActions.loadSuccess(data, pageInfo, meta.total),
            )
          })
          .catch((error) => {
            dispatchPageInfo(pageInfoActions.loadFailure(error))
          })
          .finally(() => {
            dispatchPageInfo(pageInfoActions.loadEnd())
          })
      } else {
        dispatchPageInfo(pageInfoActions.loadEnd())
      }
    } else {
      dispatchPageInfo(pageInfoActions.loadEnd())
    }
  }

  const onFetchData = (state) => {
    const pickAttrs = R.pick(['page', 'pageSize', 'sorted', 'filtered'])
    const nextPage = R.over(
      R.lensProp('page'),
      R.when(R.anyPass([isNaN, R.equals(-1)]), R.always(1)),
      pickAttrs(state),
    )
    if (equal(pickAttrs(pageInfo), nextPage)) return

    dispatchPageInfo(pageInfoActions.loadStart())
    reloadData(nextPage, searchText, filter)
  }

  // MARK : isForceResetSetInitFetchNodata If it is false.
  // MARK : It will remain firsttime init fetching nodata the same

  const refetchData = (isForceResetSetInitFetchNodata = true) => {
    if (isForceResetSetInitFetchNodata) {
      dispatchPageInfo(pageInfoActions.setInitFetchNodata(null))
    }
    dispatchPageInfo(pageInfoActions.loadStart())
    return reloadData(pageInfo, searchText, filter)
  }

  const startFetchData = () => {
    dispatchPageInfo(pageInfoActions.loadStart())
  }

  const toggleSelect = R.compose(dispatchPageInfo, pageInfoActions.toggleSelect)

  const toggleSelectAll = R.compose(
    dispatchPageInfo,
    pageInfoActions.toggleSelectAll,
  )

  const endFetchData = () => {
    dispatchPageInfo(pageInfoActions.loadEnd())
  }

  useDeepCompareEffect(() => {
    if (!R.isNil(pageInfo.data)) {
      startFetchData()
      reloadData(
        R.set(R.lensProp('page'), 0, pageInfo),
        debounceSearchText,
        filter,
      )
    }
  }, [filter])

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!R.isNil(pageInfo.data)) {
      startFetchData()
      reloadData(
        R.set(R.lensProp('page'), 0, pageInfo),
        debounceSearchText,
        filter,
      )
    }
  }, [debounceSearchText])
  /* eslint-enable react-hooks/exhaustive-deps */
  return {
    pageInfo,
    onFetchData,
    refetchData,
    startFetchData,
    endFetchData,
    showErrorDialogue,
    toggleSelect,
    toggleSelectAll,
    onChangeFilterByKey,
    onChangeFilter,
    filter,
    hasData: R.compose(
      R.not,
      R.anyPass([R.isNil, R.equals(0)]),
      R.prop('total'),
    )(pageInfo),
    showPagination: showPagination(pageInfo),
    defaultPageSize: DEFAULT_PAGE_SIZE,
    search: {
      text: searchText,
      onChange,
      onReset,
      onClearSearch,
    },
  }
}

export const showPagination = (pageInfo) => {
  if (pageInfo.data) {
    return R.path(['totalData'], pageInfo) > DEFAULT_PAGE_SIZE
  }
  return true
}

export const pageToSortPairs = R.compose(
  R.when(
    R.allPass([R.o(R.not, R.isNil), R.has('desc'), R.has('id')]),
    ({ id, desc }) => `${id}:${desc === true ? 'DESC' : 'ASC'}`,
  ),
  R.path(['sorted', 0]),
)
